import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { omit } from 'lodash-es';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';

import {
  CanvasElementId,
  isNavMenuItemsLayout,
  PartialPropsOf,
} from '@blaze-runtime/engine-domain';
import { NavMenu, RenderElementContext } from '@blaze-shared/elements-domain';
import { countedShareReplay } from '@blaze-shared/shared-util';

import { fromNavMenu } from '../+state';
import { ElementsFacade } from '../+state/elements/elements.facade';
import { toLegacySharedMenuItems } from '../+state/nav-menu/nav-menu.reducer.utils';
import { NavMenuDirection } from '../elements';

export const DEFAULT_ITEM_LABEL = 'Menu Item';
export const DEFAULT_SUBMENU_ITEM_LABEL = 'Submenu Item';
export const DEFAULT_SECTION_LABEL = 'Section';

@Injectable({ providedIn: 'root' })
export class NavMenuItemsService {
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly store = inject(Store);

  readonly entities$ = this.store.select(fromNavMenu.selectEntities);
  readonly layoutItems$: Observable<NavMenu.LegacySharedItem[]>;
  readonly storageLoaded$: Observable<boolean>;

  constructor() {
    this.layoutItems$ = this.store.select(fromNavMenu.getNavMenuState).pipe(
      map((state) => toLegacySharedMenuItems(state)),
      countedShareReplay(),
    );

    this.storageLoaded$ = this.elementsFacade.layoutIds$.pipe(
      concatLatestFrom(() => this.elementsFacade.layoutEntities$),
      map(([ids, entities]) =>
        ids.some((id) => {
          const layout = entities[id];
          return isNavMenuItemsLayout(layout);
        }),
      ),
      distinctUntilChanged(),
    );
  }

  observeRenderMenuItems(
    storage$: Observable<NavMenu.LocalStorage | undefined>,
  ): Observable<NavMenu.RenderItem[]> {
    return combineLatest({
      storage: storage$,
      sharedItems: this.entities$,
    }).pipe(map(({ storage, sharedItems }) => makeMenuItemsReadyForRender(storage, sharedItems)));
  }

  addLocalItem(placement: NavMenu.Placement, itemType: NavMenu.ItemType): void {
    this.store.dispatch(fromNavMenu.addLocalItem({ placement, itemType }));
  }

  addSharedItem(placement: NavMenu.Placement): void {
    this.store.dispatch(fromNavMenu.requestAddSharedItem({ placement }));
  }

  editItemByPath(id: string, propName: keyof NavMenu.ItemProps, value: any): void {
    this.store.dispatch(fromNavMenu.editItemByPath({ id, propName, value }));
  }

  deleteItem(id: string, elementId: string): void {
    this.store.dispatch(fromNavMenu.requestDeleteItem({ id, elementId }));
  }

  removeSection(id: string, elementId: string): void {
    this.store.dispatch(fromNavMenu.removeSection({ id, elementId }));
  }

  markItemAsDirty(id: string): void {
    this.store.dispatch(fromNavMenu.markItemAsDirty({ id }));
  }

  moveItem(
    elementId: CanvasElementId,
    dragRef: NavMenu.RefIds,
    targetRef: NavMenu.RefIds,
    direction: NavMenu.DropDirection,
  ): void {
    this.store.dispatch(fromNavMenu.moveItem({ elementId, dragRef, targetRef, direction }));
  }
}

export function isLegacyNavMenuItemReference(
  item?: NavMenu.LegacyPropertiesConfigInputNavMenuItem,
): item is NavMenu.LegacyReference {
  return !!item && 'targetId' in item;
}

export function isLegacyNavMenuSharedItem(item?: {
  id: string;
  ns: string;
  props?: any;
}): item is NavMenu.LegacySharedItem {
  return (
    !!item &&
    typeof item.props === 'object' &&
    'items' in item.props &&
    Array.isArray(item.props.items)
  );
}

export function isNavMenuLocalItem(item?: NavMenu.ItemBase): item is NavMenu.LocalItem {
  return !!item && item.type === 'localItem';
}

export function isNavMenuItemReference(item?: NavMenu.ItemBase): item is NavMenu.ReferenceItem {
  return !!item && item.type === 'reference';
}

export function isNavMenuResolvedItemReference(item?: any): item is NavMenu.ResolvedReferenceItem {
  return !!item && item.type === 'reference' && 'targetId' in item && item.targetId !== undefined;
}

export function isNavMenuSection(item?: NavMenu.ItemBase): item is NavMenu.SectionItem {
  return !!item && item.type === 'section';
}

export function areSectionsPresent(storage?: NavMenu.LocalStorage): boolean {
  if (!storage) {
    return false;
  }
  return Object.values(storage).some((item) => isNavMenuSection(item));
}

const sharedMenuItemPropertyKeys = new Set<keyof NavMenu.ItemProps>([
  'label',
  'actionParams',
  'clickAction',
  'icon',
  'navigateUrl',
  'visibilityExpression',
  'visible',
]);

export function isSharedMenuItemPropertyKey(key?: string): key is keyof NavMenu.ItemProps {
  return !!key && sharedMenuItemPropertyKeys.has(key as keyof NavMenu.ItemProps);
}

export function getRefIds(item: NavMenu.RenderItem): NavMenu.RefIds {
  if (isNavMenuResolvedItemReference(item)) {
    return {
      localId: item.id,
      sharedId: item.targetId,
      parentLocalId: item.parentId,
      parentSharedId: item.parentTargetId,
    };
  }

  return {
    localId: item.id,
    parentLocalId: item.parentId,
  };
}

export function migrateLegacyNavMenuItemsMutably(props: PartialPropsOf<'navMenu'>): void {
  if (props.content?.storage || !props.content?.items) {
    return;
  }

  const legacyItems = props.content?.items;
  const storage = generateNewLocalStorage();

  for (const legacyItem of legacyItems) {
    const migratedItem = migrateLegacyToStorageItem(legacyItem);

    if (!isLegacyNavMenuItemReference(legacyItem) && legacyItem.items?.length) {
      migratedItem.children = [];
      for (const legacySubMenuItem of legacyItem.items) {
        const migratedSubMenuItem = migrateLegacyToStorageItem(legacySubMenuItem);
        storage[migratedSubMenuItem.id] = migratedSubMenuItem;
        migratedItem.children.push(migratedSubMenuItem.id);
      }
    }

    storage[migratedItem.id] = migratedItem;
    storage[NavMenu.ROOT_LOCAL_ITEM_ID].children.push(migratedItem.id);
  }

  props.content.storage = storage;
}

export function migrateLegacyToStorageItem(
  legacyItem: NavMenu.LegacyPropertiesConfigInputNavMenuItem,
): NavMenu.MenuItem {
  if (isLegacyNavMenuItemReference(legacyItem)) {
    return {
      id: crypto.randomUUID(),
      type: 'reference',
      targetId: legacyItem.targetId,
      childrenMeta: legacyItem.submenuItemsMeta,
    } satisfies NavMenu.ReferenceItem;
  }

  return {
    id: crypto.randomUUID(),
    type: 'localItem',
    props: omit(legacyItem, ['items']),
  } satisfies NavMenu.LocalItem;
}

function makeNewMenuItemId(namespaceId: string): string {
  return `${namespaceId}_${crypto.randomUUID()}`;
}

export function generateNewLocalStorage(): NavMenu.LocalStorage {
  return {
    [NavMenu.ROOT_LOCAL_ITEM_ID]: {
      id: NavMenu.ROOT_LOCAL_ITEM_ID,
      type: 'localItem',
      children: [],
    },
  };
}

export function generateNewSharedItem(
  namespaceId: string,
  initProps?: Partial<NavMenu.ItemProps>,
  isPristine = true,
): NavMenu.SharedItem {
  const item: NavMenu.SharedItem = {
    id: makeNewMenuItemId(namespaceId),
    ns: namespaceId,
    isPristine,
    props: {
      label: DEFAULT_ITEM_LABEL,
      ...initProps,
    },
  };

  if (!isPristine) {
    delete item.isPristine;
  }

  return item;
}

export function generateNewLocalSubmenuItem(
  initProps?: Partial<NavMenu.ItemProps>,
): NavMenu.LocalItem {
  return {
    id: crypto.randomUUID(),
    type: 'localItem',
    props: {
      label: DEFAULT_SUBMENU_ITEM_LABEL,
      ...initProps,
    },
  };
}

export function generateNewItemReference(targetId: string): NavMenu.ReferenceItem {
  return {
    id: crypto.randomUUID(),
    type: 'reference',
    targetId,
  };
}

export function generateNewSectionItem(
  initProps?: Partial<NavMenu.SectionItemProps>,
): NavMenu.SectionItem {
  return {
    id: crypto.randomUUID(),
    type: 'section',
    props: {
      label: DEFAULT_SECTION_LABEL,
      ...initProps,
    },
  };
}

export function makeMenuItemsReadyForRender(
  localStorage: NavMenu.LocalStorage | undefined,
  sharedStorage: NavMenu.SharedStorage,
): NavMenu.RenderItem[] {
  const root = localStorage?.[NavMenu.ROOT_LOCAL_ITEM_ID];

  if (!root) {
    return [];
  }

  return root.children
    .map((id) => makeMenuItemReadyForRender(id, localStorage, sharedStorage))
    .filter(Boolean) as NavMenu.RenderItem[];
}

export function makeMenuItemsRenderStorage(
  localStorage: NavMenu.LocalStorage | undefined,
  sharedStorage: NavMenu.SharedStorage,
): NavMenu.RenderStorage {
  const renderStorage: NavMenu.RenderStorage = {};
  const startElement = localStorage?.[NavMenu.ROOT_LOCAL_ITEM_ID];

  if (!startElement) {
    return renderStorage;
  }

  const addRenderChildren = (children?: NavMenu.RenderItem[]): void => {
    for (const child of children ?? []) {
      renderStorage[child.id] = child;
      addRenderChildren(child.children);
    }
  };

  for (const id of startElement.children ?? []) {
    const item = makeMenuItemReadyForRender(id, localStorage, sharedStorage);
    if (!item) {
      continue;
    }
    renderStorage[id] = item;
    addRenderChildren(item.children);
  }

  return renderStorage;
}

export function makeMenuItemReadyForRender(
  itemId: string,
  localStorage: NavMenu.LocalStorage,
  sharedStorage: NavMenu.SharedStorage,
): NavMenu.RenderItem | undefined {
  const item = localStorage[itemId];

  if (!item) {
    return undefined;
  }

  const renderItem: NavMenu.RenderItem = { ...item, children: undefined };

  if (item.type === 'reference') {
    const sharedItem = sharedStorage[item.targetId];

    if (!sharedItem) {
      return undefined;
    }

    renderItem.props = { ...omit(sharedItem.props, ['items']) };
    renderItem.children = sharedItem.children
      ?.filter((subMenuId) => !item.childrenMeta?.[subMenuId]?.isHidden)
      .map((subMenuId) => {
        const submenuItem = sharedStorage[subMenuId];

        if (!submenuItem) {
          return undefined;
        }

        return {
          id: subMenuId,
          targetId: subMenuId,
          type: 'reference',
          parentId: item.id,
          parentTargetId: item.targetId,
          props: submenuItem.props,
        } satisfies NavMenu.RenderItem;
      })
      .filter((item) => !!item);

    return renderItem;
  }

  renderItem.children = item.children
    ?.map((childId) => makeMenuItemReadyForRender(childId, localStorage, sharedStorage))
    .filter(Boolean) as NavMenu.RenderItem[];

  return renderItem;
}

export function findReferenceItemByTargetId(
  id: string,
  storage: NavMenu.LocalStorage,
): NavMenu.ReferenceItem | undefined {
  return Object.values(storage).find(
    (item) => isNavMenuItemReference(item) && item.targetId === id,
  ) as NavMenu.ReferenceItem | undefined;
}

export function calculateNavMenuDirection(
  props: PartialPropsOf<'navMenu'>,
  renderElementContext?: RenderElementContext,
): NavMenuDirection | undefined {
  const { direction, autoDirection } = props.content || {};

  if (!autoDirection || !renderElementContext?.navSection) {
    return direction;
  }

  if (renderElementContext.navSection === 'header') {
    return 'horizontal';
  }
  if (renderElementContext.navSection === 'side') {
    return 'vertical';
  }
  return direction;
}
